import { AbstractFreewheelClass } from 'tag/ts/tag/ads/freewheel/abstract/AbstractFreewheelClass';
import { FreewheelParameterType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelParameterType';
import { FreewheelRequestType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelRequestType';
import { FmsParameter } from 'tag/ts/tag/ads/freewheel/parameters/FmsParameter';
export class AbstractFreewheelRequest extends AbstractFreewheelClass {
    type;
    #excludedFMSKeys = [
        '_fw_app_bundle',
        '_fw_atts',
        '_fw_did',
        '_fw_is_lat',
        '_fw_nielsen_app_id',
        'fms_idfv',
        'fms_ifa',
        'imafw__fw_app_bundle',
        'imafw__fw_atts',
        'imafw__fw_did',
        'imafw__fw_is_lat',
        'region',
        'imafw_region'
    ];
    #parameters;
    #daiPrefix = 'imafw_';
    constructor(argument) {
        super({
            ...argument,
            name: 'AbstractFreewheelRequest'
        });
        this.type = argument.type;
        this.#parameters = [];
        argument.parameters?.forEach((p) => this.addParameter(p));
    }
    get parameters() {
        return this.#parameters;
    }
    isDaiRequest() {
        return [
            FreewheelRequestType.DAI_API,
            FreewheelRequestType.DAI_SDK,
            FreewheelRequestType.DAI_SSB
        ].includes(this.type);
    }
    getParameter(key) {
        if (!(this.utils.Type.isString(key) && key.length > 0)) {
            this?.logger?.warn('getParameter', 'invalid key:', key);
            return;
        }
        const foundParameter = this.#parameters.find((p) => p.key === key) || undefined;
        if (this.utils.Type.isEmpty(foundParameter)) {
            this?.logger?.log('getParameter', 'parameter not found', 'key:', key);
            return;
        }
        return foundParameter;
    }
    getParametersByType(types) {
        return this.#parameters.filter((param) => types.includes(param.type));
    }
    getGlobalParameters() {
        return this.#parameters.filter((param) => param.type === FreewheelParameterType.GLOBAL);
    }
    getSiteParameters() {
        return this.#parameters.filter((param) => param.type === FreewheelParameterType.SITE);
    }
    getCustomParameters() {
        return this.#parameters.filter((param) => param.type === FreewheelParameterType.CUSTOM);
    }
    addFmsParameters(fms) {
        this?.logger?.log('addFmsParameters', 'creating parameters from FMS response...', '\nfms:', fms, '\nkeys to filter out:', this.#excludedFMSKeys);
        if (this.utils.Type.isEmpty(fms) || !this.utils.Type.isObject(fms)) {
            this?.logger?.warn('addFmsParameters', 'missing/invalid FMS object:', fms);
            return;
        }
        Object.keys(fms)
            .filter((key) => {
            if (this.#excludedFMSKeys.includes(key)) {
                this?.logger?.log('addFmsParameters', 'filtering out excluded FMS parameter...', '\nkey:', key, '\nvalue:', fms[key]);
                return false;
            }
            if (this.utils.Type.isNullOrUndefined(fms[key])) {
                this?.logger?.log('addFmsParameters', 'filtering out null/undefined FMS parameter...', '\nkey:', key, '\nvalue:', fms[key]);
                return false;
            }
            return true;
        })
            .map((key) => {
            if (this.isDaiRequest() && !key.startsWith(this.#daiPrefix)) {
                return new FmsParameter(`${this.#daiPrefix}${key}`, fms[key]);
            }
            return new FmsParameter(key, fms[key]);
        })
            .forEach((fmsParam) => {
            const preexistingParam = this.getParameter(fmsParam.key);
            if (!this.utils.Type.isEmpty(preexistingParam)) {
                this?.logger?.warn('addFmsParameters', 'adding FMS parameter with duplicate key as a pre-existing parameter...', '\nduplicate key: ', fmsParam.key, '\nFMS param: ', fmsParam, '\npre-existing param: ', preexistingParam);
            }
            this.addParameter(fmsParam);
        });
    }
    addParameter(parameter) {
        if (this.utils.Type.isNullOrUndefined(parameter?.type)) {
            this.logger.error('addParameter', 'parameter is missing type, not adding:', parameter);
            return;
        }
        if (this.utils.Type.isNullOrUndefined(parameter?.key)) {
            this.logger.error('addParameter', 'parameter is missing key, not adding:', parameter);
            return;
        }
        if (this.utils.Type.isNullOrUndefined(parameter?.value)) {
            this?.logger?.log('addParameter', 'parameter is missing value (could be an error or intended to be omitted), not adding:', parameter);
            return;
        }
        this.#parameters.push(parameter);
        this?.logger?.log('addParameter', 'added:', parameter, 'parameters:', this.#parameters);
    }
    removeParameter(key) {
        if (!(this.utils.Type.isString(key) && key.length > 0)) {
            this?.logger?.warn('removeParameter', 'invalid key:', key);
            return;
        }
        const foundParameter = this.getParameter(key);
        if (this.utils.Type.isEmpty(foundParameter)) {
            this?.logger?.log('removeParameter', 'parameter not found', 'key:', key);
            return;
        }
        this.#parameters.splice(this.#parameters.indexOf(foundParameter), 1);
        this?.logger?.log('removeParameter', 'removed:', foundParameter, '\nparameters:', this.#parameters);
        return foundParameter;
    }
}
